import { useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ApiError } from '../lib/api';
import {
  getStory,
  listStories,
  type StoryListItem,
} from '../lib/stories';
import { getStepLabelKey, getStepPct, isInProgress } from '../lib/storyStatus';
import { useStoryProgress } from '../lib/useStoryProgress';
import { CostBadge } from '../components/CostBadge';
import { STORY_COST } from '../data/costs';
import '../components/wizard/wizard.css';
import './Stories.css';

type LoadState =
  | { kind: 'loading' }
  | { kind: 'ready'; stories: StoryListItem[] }
  | { kind: 'unauthenticated' }
  | { kind: 'error'; message: string };

type LocationState = { optimisticStory?: StoryListItem } | null;

export function Stories() {
  const { t } = useTranslation();
  const [state, setState] = useState<LoadState>({ kind: 'loading' });
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;
    listStories()
      .then((stories) => {
        if (!cancelled) setState({ kind: 'ready', stories });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        if (err instanceof ApiError && (err.status === 401 || err.status === 403)) {
          setState({ kind: 'unauthenticated' });
          return;
        }
        const message = err instanceof Error ? err.message : t('common.unknownError');
        setState({ kind: 'error', message });
      });
    return () => {
      cancelled = true;
    };
  }, [t]);

  useEffect(() => {
    const incoming = location.state as LocationState;
    if (!incoming?.optimisticStory) return;
    const stub = incoming.optimisticStory;
    setState((prev) =>
      prev.kind === 'ready' && !prev.stories.some((s) => s.id === stub.id)
        ? { ...prev, stories: [stub, ...prev.stories] }
        : prev,
    );
    navigate(location.pathname, { replace: true, state: null });
  }, [location, navigate]);

  function replaceStory(updated: StoryListItem) {
    setState((prev) =>
      prev.kind === 'ready'
        ? {
            ...prev,
            stories: prev.stories.map((s) => (s.id === updated.id ? updated : s)),
          }
        : prev,
    );
  }

  return <Body state={state} onStoryUpdate={replaceStory} />;
}

function Body({
  state,
  onStoryUpdate,
}: {
  state: LoadState;
  onStoryUpdate: (s: StoryListItem) => void;
}) {
  const { t } = useTranslation();
  switch (state.kind) {
    case 'loading':
      return <p className="story-status">{t('common.loading')}</p>;
    case 'unauthenticated':
      return (
        <p className="story-status">
          <Trans i18nKey="common.notSignedIn" components={[<a href="/" key="0" />]} />
        </p>
      );
    case 'error':
      return (
        <p className="story-status story-status--error">
          {t('common.error', { message: state.message })}
        </p>
      );
    case 'ready':
      if (state.stories.length === 0) {
        return (
          <div className="empty-cta">
            <Link
              to="/stories/new"
              className="round-create round-create--large"
              aria-label={t('stories.emptyCreate')}
            >
              <span className="round-create__plus" aria-hidden="true">+</span>
            </Link>
            <p className="empty-cta__label">{t('stories.emptyCreate')}</p>
            <p className="empty-cta__hint">{t('stories.emptyHint')}</p>
          </div>
        );
      }
      return (
        <>
          <div className="list-header">
            <h1>{t('stories.listHeading')}</h1>
            <Link to="/stories/new" className="create-cta">
              <span className="create-cta__icon" aria-hidden="true">+</span>
              <span className="create-cta__label">{t('stories.createCta')}</span>
              <CostBadge cost={STORY_COST} />
            </Link>
          </div>
          <StoryList stories={state.stories} onStoryUpdate={onStoryUpdate} />
        </>
      );
  }
}

function StoryList({
  stories,
  onStoryUpdate,
}: {
  stories: StoryListItem[];
  onStoryUpdate: (s: StoryListItem) => void;
}) {
  return (
    <ul className="story-list">
      {stories.map((s) => (
        <StoryRow key={s.id} story={s} onStoryUpdate={onStoryUpdate} />
      ))}
    </ul>
  );
}

function StoryRow({
  story,
  onStoryUpdate,
}: {
  story: StoryListItem;
  onStoryUpdate: (s: StoryListItem) => void;
}) {
  const { t } = useTranslation();
  if (story.status === 'failed') {
    return (
      <li className="story-item story-item--failed">
        <h3>{t('stories.generationFailed')}</h3>
        <p className="story-item__error">{story.error_message}</p>
        <div className="meta">{formatDate(story.created_at)}</div>
      </li>
    );
  }
  if (story.status === 'complete') {
    return (
      <li className="story-item">
        <h3>
          <Link to={`/stories/${story.id}`}>{story.title}</Link>
        </h3>
        <p>{truncateWords(story.synopsis, 30)}</p>
        {story.heroes.length > 0 && (
          <div className="story-heroes">
            {story.heroes.map((h) => (
              <img
                key={h.id}
                src={h.photo}
                alt={h.name}
                title={h.name}
                className="story-hero-thumb"
              />
            ))}
          </div>
        )}
        <div className="meta">{formatDate(story.created_at)}</div>
      </li>
    );
  }
  return <InFlightStory story={story} onStoryUpdate={onStoryUpdate} />;
}

function InFlightStory({
  story,
  onStoryUpdate,
}: {
  story: StoryListItem;
  onStoryUpdate: (s: StoryListItem) => void;
}) {
  const { t } = useTranslation();
  const event = useStoryProgress(story.id);
  const status = event?.status ?? story.status;
  const detail = event?.detail;

  useEffect(() => {
    if (status !== 'complete') return;
    let cancelled = false;
    getStory(story.id)
      .then((full) => {
        if (cancelled) return;
        onStoryUpdate({
          ...story,
          status: 'complete',
          title: full.title,
          synopsis: full.synopsis,
        });
      })
      .catch(() => {
        if (!cancelled) window.location.href = `/stories/${story.id}/`;
      });
    return () => {
      cancelled = true;
    };
  }, [status, story, onStoryUpdate]);

  useEffect(() => {
    if (status === 'failed') {
      onStoryUpdate({
        ...story,
        status: 'failed',
        error_message: detail ?? t('stories.generationFailedDefault'),
      });
    }
  }, [status, detail, story, onStoryUpdate, t]);

  if (!isInProgress(status)) {
    return null;
  }

  return (
    <li className="story-item story-item--generating" data-story-id={story.id}>
      <h3>{t('stories.generating')}</h3>
      {story.heroes.length > 0 && (
        <div className="story-heroes">
          {story.heroes.map((h) => (
            <img
              key={h.id}
              src={h.photo}
              alt={h.name}
              title={h.name}
              className="story-hero-thumb"
            />
          ))}
        </div>
      )}
      <div className="progress-container">
        <div className="progress-bar">
          <div className="progress-fill" style={{ width: `${getStepPct(status)}%` }} />
        </div>
        <div className="progress-label">{detail || t(getStepLabelKey(status))}</div>
        <div className="progress-note">{t('stories.emailNotice')}</div>
      </div>
    </li>
  );
}

function truncateWords(s: string, n: number): string {
  const parts = s.split(/\s+/);
  if (parts.length <= n) return s;
  return parts.slice(0, n).join(' ') + '…';
}

const DATE_FMT = new Intl.DateTimeFormat(undefined, {
  month: 'short',
  day: 'numeric',
  year: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
});

function formatDate(iso: string): string {
  try {
    return DATE_FMT.format(new Date(iso));
  } catch {
    return iso;
  }
}
